import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import './App.css';
import { fetchAccessStatus } from './utils/siteAccess';
import animations from './utils/animations';
import Header from './components/Header';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';
import { UserProvider } from './contexts/UserContext';
import { CartProvider } from './contexts/CartContext';
import { FavoritesProvider } from './contexts/FavoritesContext';
import { ToastProvider } from './contexts/ToastContext';
import Home from './pages/Home';
import About from './pages/About';
import Space from './pages/Space';
import Contact from './pages/Contact';
import Products from './pages/Products';
import ProductDetail from './pages/ProductDetail';
import Cart from './pages/Cart';
import Login from './pages/Login';
import FAQs from './pages/FAQs';
import WhyVintage from './pages/WhyVintage';
import Outfits from './pages/Outfits';
import Catalog from './pages/Catalog';
import Favorites from './pages/Favorites';
import Account from './pages/Account';
import Checkout from './pages/Checkout';
import OrderSuccess from './pages/OrderSuccess';
import Policies from './pages/Policies';
import Legal from './pages/Legal';
import ComingSoon from './pages/ComingSoon';
import AdminDashboard from './pages/AdminDashboard';
import AdminProducts from './pages/AdminProducts';
import AdminOrders from './pages/AdminOrders';
import AdminUsers from './pages/AdminUsers';
import AdminContent from './pages/AdminContent';
import AdminFitReferences from './pages/AdminFitReferences';
import AddProduct from './pages/AddProduct';
import AdminProtectedRoute from './components/AdminProtectedRoute';

// Gates the public site behind /coming-soon while COMING_SOON_MODE is on
// server-side. Admin routes and /coming-soon itself sit outside this gate.
function SiteGate() {
  const [state, setState] = useState<'checking' | 'locked' | 'open'>('checking');

  useEffect(() => {
    let cancelled = false;
    fetchAccessStatus().then(({ comingSoonMode, unlocked }) => {
      if (cancelled) return;
      setState(comingSoonMode && !unlocked ? 'locked' : 'open');
    });
    return () => {
      cancelled = true;
    };
  }, []);

  if (state === 'checking') return null;
  if (state === 'locked') return <Navigate to="/coming-soon" replace />;
  return <Outlet />;
}

function App() {
  // Initialize animations on app mount
  useEffect(() => {
    animations.init();
    
    // Cleanup on unmount
    return () => {
      animations.cleanup();
    };
  }, []);

  return (
    <Router>
      <UserProvider>
        <CartProvider>
        <FavoritesProvider>
        <ToastProvider>
          <div className="App">
            <ScrollToTop />
            <Routes>
              {/* Admin Routes - admins sign in through the regular /login form */}
              <Route path="/admin/login" element={<Navigate to="/login" replace />} />
              <Route path="/admin/dashboard" element={
                <AdminProtectedRoute>
                  <AdminDashboard />
                </AdminProtectedRoute>
              } />
              <Route path="/admin/products" element={
                <AdminProtectedRoute>
                  <AdminProducts />
                </AdminProtectedRoute>
              } />
              <Route path="/admin/products/add" element={
                <AdminProtectedRoute>
                  <AddProduct />
                </AdminProtectedRoute>
              } />
              <Route path="/admin/orders" element={
                <AdminProtectedRoute>
                  <AdminOrders />
                </AdminProtectedRoute>
              } />
              <Route path="/admin/users" element={
                <AdminProtectedRoute>
                  <AdminUsers />
                </AdminProtectedRoute>
              } />
              <Route path="/admin/fit-references" element={
                <AdminProtectedRoute>
                  <AdminFitReferences />
                </AdminProtectedRoute>
              } />
              <Route path="/admin/content" element={
                <AdminProtectedRoute>
                  <AdminContent />
                </AdminProtectedRoute>
              } />
              <Route path="/admin/*" element={
                <AdminProtectedRoute>
                  <AdminDashboard />
                </AdminProtectedRoute>
              } />
              
              {/* Coming Soon (outside the gate — always reachable) */}
              <Route path="/coming-soon" element={<ComingSoon />} />

              {/* Public Routes — gated behind Coming Soon while COMING_SOON_MODE is on */}
              <Route element={<SiteGate />}>
              <Route path="/" element={
                <>
                  <Header />
                  <Home />
                  <Footer />
                </>
              } />
              <Route path="/about" element={
                <>
                  <Header />
                  <About />
                  <Footer />
                </>
              } />
              <Route path="/space" element={
                <>
                  <Header />
                  <Space />
                  <Footer />
                </>
              } />
              <Route path="/contact" element={
                <>
                  <Header />
                  <Contact />
                  <Footer />
                </>
              } />
              <Route path="/shop" element={
                <>
                  <Header />
                  <Products />
                  <Footer />
                </>
              } />
              <Route path="/product/:id" element={
                <>
                  <Header />
                  <ProductDetail />
                  <Footer />
                </>
              } />
              <Route path="/cart" element={
                <>
                  <Header />
                  <Cart />
                  <Footer />
                </>
              } />
              <Route path="/login" element={
                <>
                  <Header />
                  <Login />
                  <Footer />
                </>
              } />
              <Route path="/faqs" element={
                <>
                  <Header />
                  <FAQs />
                  <Footer />
                </>
              } />
              <Route path="/vintage" element={
                <>
                  <Header />
                  <WhyVintage />
                  <Footer />
                </>
              } />
              <Route path="/outfits" element={
                <>
                  <Header />
                  <Outfits />
                  <Footer />
                </>
              } />
              <Route path="/catalog" element={
                <>
                  <Header />
                  <Catalog />
                  <Footer />
                </>
              } />
              <Route path="/favorites" element={
                <>
                  <Header />
                  <Favorites />
                  <Footer />
                </>
              } />
              <Route path="/account" element={
                <>
                  <Header />
                  <Account />
                  <Footer />
                </>
              } />
              <Route path="/checkout" element={
                <>
                  <Header />
                  <Checkout />
                  <Footer />
                </>
              } />
              <Route path="/order-success/:orderNumber" element={
                <>
                  <Header />
                  <OrderSuccess />
                  <Footer />
                </>
              } />
              <Route path="/orders" element={
                <>
                  <Header />
                  <Account />
                  <Footer />
                </>
              } />
              <Route path="/settings" element={
                <>
                  <Header />
                  <Account />
                  <Footer />
                </>
              } />
              <Route path="/shipping" element={
                <>
                  <Header />
                  <Policies />
                  <Footer />
                </>
              } />
              <Route path="/exchanges" element={
                <>
                  <Header />
                  <Policies />
                  <Footer />
                </>
              } />
              <Route path="/policies" element={
                <>
                  <Header />
                  <Policies />
                  <Footer />
                </>
              } />
              <Route path="/privacy" element={
                <>
                  <Header />
                  <Legal />
                  <Footer />
                </>
              } />
              <Route path="/terms" element={
                <>
                  <Header />
                  <Legal />
                  <Footer />
                </>
              } />
              <Route path="/cookies" element={
                <>
                  <Header />
                  <Legal />
                  <Footer />
                </>
              } />
              </Route>
            </Routes>
          </div>
        </ToastProvider>
        </FavoritesProvider>
        </CartProvider>
      </UserProvider>
    </Router>
  );
}

export default App;
